import { Link } from "react-router-dom";
import { ScrollText } from "lucide-react";
import { usePageMeta } from "../hooks/usePageMeta";

export default function TermsPage() {
  usePageMeta({
    path: "/terms",
    title: "Terms of use , MediTalk",
    description:
      "Terms of use for the MediTalk platform, operated by Medicys Limited. What you agree to when you use meditalk.world.",
  });

  return (
    <div className="bg-white">
      <section className="bg-[var(--dark-bg)] grain-dark relative overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute -top-32 -right-24 w-[420px] h-[420px] rounded-full bg-[var(--lime)]/10 blur-3xl pointer-events-none"
        />
        <div className="container-main py-12 md:py-16 relative z-10 max-w-3xl">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold text-[var(--lime)] mb-3">
            <ScrollText size={14} />
            Legal
          </span>
          <h1 className="font-['Outfit'] font-bold text-white tracking-tight text-3xl md:text-5xl mb-4 leading-[1.05]">
            Terms of use
          </h1>
          <p className="text-base md:text-lg text-white/80 leading-relaxed max-w-2xl">
            The rules for using the MediTalk platform. By using meditalk.world
            you agree to the points below.
          </p>
          <p className="text-[12.5px] text-white/55 mt-5">Last updated: February 2026</p>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="container-main max-w-3xl">
          <div className="text-[15px] md:text-[15.5px] text-[var(--text-muted)] leading-relaxed space-y-7">
            <p>
              MediTalk (the &ldquo;platform&rdquo; or &ldquo;site&rdquo;) is operated by{" "}
              <strong>Medicys<span className="text-[0.55em] align-top tracking-normal ml-px" aria-label="registered trademark">®</span> Limited</strong>, a company registered in England
              &amp; Wales (company number <strong>04504403</strong>), with its
              registered office at 2nd Floor Medway Bridge House, 1–8
              Fairmeadow, Maidstone, Kent, United Kingdom, ME14 1JP. VAT number{" "}
              <strong>GB 799 9924 23</strong>. ICO registration{" "}
              <strong>Z1909295</strong>. &ldquo;Medicys&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo; and &ldquo;our&rdquo; refer
              to Medicys Limited.
            </p>

            <Section title="1. About these terms">
              <p>
                These terms govern your access to and use of meditalk.world.
                They sit alongside our{" "}
                <Link to="/privacy" className="ml-policy-link">
                  privacy notice
                </Link>{" "}
                and{" "}
                <Link to="/cookies" className="ml-policy-link">
                  cookie policy
                </Link>
                . By browsing the site, signing up as a patient or caregiver,
                expressing interest in a study or otherwise using MediTalk, you
                accept these terms. If you don&apos;t accept them, please don&apos;t use
                the site.
              </p>
            </Section>

            <Section title="2. Who can use MediTalk">
              <p>
                You must be 18 or over to register a MediTalk account. If you
                are a caregiver registering on behalf of a person you care for
                who is under 18 or otherwise vulnerable, you confirm you have
                the legal authority to provide their information for research
                purposes and that they (where capable) understand and agree to
                that.
              </p>
              <p>
                You agree to provide truthful, accurate information about
                yourself and (for caregivers) the people you care for. We may
                suspend or close accounts where the information provided
                appears to be falsified, duplicated, or used to commit fraud.
              </p>
            </Section>

            <Section title="3. What MediTalk does">
              <p>
                MediTalk is a healthcare-research recruitment platform. We
                connect patients and caregivers with paid research studies run
                or managed by Medicys on behalf of healthcare clients,
                academic researchers and other sponsors. MediTalk does not
                provide medical advice, diagnosis or treatment. Information
                shared during studies is for research purposes only and is not
                a substitute for advice from a qualified healthcare
                professional.
              </p>
            </Section>

            <Section title="4. Your responsibilities">
              <ul className="ml-list">
                <li>Keep your contact details up to date via the &ldquo;Update my info&rdquo; magic-link flow.</li>
                <li>Do not share your magic-link emails with anyone else. They give one-click access to your account.</li>
                <li>Use the site lawfully and respect the rights of other users, research participants, sponsors and our team.</li>
                <li>Do not attempt to disrupt the service, scrape data, reverse-engineer the platform, or upload malicious code.</li>
                <li>Honour the commitments you make when expressing interest in a study (attend scheduled sessions, complete agreed tasks, etc.).</li>
              </ul>
            </Section>

            <Section title="5. Payments and rewards">
              <p>
                Where a study offers a thank-you payment or honorarium, the
                amount, eligibility criteria and conditions are described on
                the study page and in the study-specific consent form. Medicys
                processes payments through payment partners (for example
                e-voucher providers and our bank, Lloyds). You consent to your
                contact and payment details being shared with these processors
                where it is necessary to pay you.
              </p>
            </Section>

            <Section title="6. Intellectual property">
              <p>
                The MediTalk name, logo, design, content and code are owned by
                Medicys Limited and protected by UK and international
                intellectual-property law. You may use the site for its
                intended purpose (browsing studies, registering, taking part
                in research) but you may not copy, reproduce or commercially
                exploit any part of the site without our written permission.
              </p>
            </Section>

            <Section title="7. Third-party services and links">
              <p>
                The platform integrates with selected third-party services to
                operate (for example: Resend for transactional email, payment
                processors, and screening or interview tooling provided per
                study). Where we link to external websites, those sites are
                outside our control and have their own terms and privacy
                practices.
              </p>
            </Section>

            <Section title="8. Availability and changes">
              <p>
                We aim to keep MediTalk available 24/7 but we cannot guarantee
                uninterrupted access. We may add, change or remove features at
                any time. Where a change affects how your data is processed,
                we will update the{" "}
                <Link to="/privacy" className="ml-policy-link">
                  privacy notice
                </Link>{" "}
                and, where required, notify you.
              </p>
            </Section>

            <Section title="9. Liability">
              <p>
                To the fullest extent permitted by law, Medicys Limited
                excludes all implied warranties and is not liable for any
                indirect, incidental, special or consequential loss arising
                from your use of the site. Nothing in these terms limits
                liability we cannot lawfully exclude (for example for fraud,
                death or personal injury caused by our negligence, or
                liability under UK consumer law that cannot be waived).
              </p>
            </Section>

            <Section title="10. Termination">
              <p>
                You may close your MediTalk account at any time by emailing{" "}
                <a className="ml-policy-link" href="mailto:privacy@medicysltd.co.uk">
                  privacy@medicysltd.co.uk
                </a>
                . We may suspend or close an account if we reasonably believe
                you have breached these terms, provided false information, or
                are using the platform unlawfully. Closing an account does not
                automatically delete records we are legally required to keep
                (for example for adverse-event reporting) , see the{" "}
                <Link to="/privacy" className="ml-policy-link">
                  privacy notice
                </Link>{" "}
                for retention details.
              </p>
            </Section>

            <Section title="11. Governing law">
              <p>
                These terms are governed by the laws of England and Wales. Any
                dispute will be subject to the exclusive jurisdiction of the
                courts of England and Wales, except that you may bring
                proceedings in the country where you live if local law
                requires it.
              </p>
            </Section>

            <Section title="12. Contact">
              <p>
                Questions about these terms? Email{" "}
                <a className="ml-policy-link" href="mailto:hello@meditalk.world">
                  hello@meditalk.world
                </a>{" "}
                or write to Medicys Limited at the registered office above.
              </p>
            </Section>
          </div>
        </div>
      </section>
    </div>
  );
}

const Section = ({ title, children }) => (
  <section>
    <h2 className="font-['Outfit'] font-bold text-[var(--charcoal)] text-xl md:text-2xl mb-3">
      {title}
    </h2>
    <div className="space-y-3">{children}</div>
  </section>
);
